import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DropzoneArea } from "react-mui-dropzone";
import "./../../../styles/scss/common.scss";
import parse from "html-react-parser";
import { ContainerStyled } from "./uploadComponent.style";
import HlxButton from '../../commonComponents/button/button';
import UptimeGeneralDialog from '../../dialogComponents/generalDialog/generalDialog';
import { Tooltip, TooltipProps } from "@mui/material";
import styled from "styled-components";

// const mimeType = {
//   sql: "application/sql",
//   vbs: "application/x-vbscript",
//   ini: "text/plain",
//   reg: "text/plain",
//   iso: "application/iso",
//   exe: "application/x-msdownload",
//   js: "text/javascript",
//   bin: "application/octet-stream",
//   swf: "application/x-shockwave-flash",
//   sh: "application/x-sh",
//   java: "text/x-java-source,java",
// };
const ToBeStyledTooltip = (props: TooltipProps) => (
  <Tooltip classes={{ popper: props.className }} {...props} />
);

const StyledTooltip = styled(ToBeStyledTooltip)`
${({theme}) =>`
  & .MuiTooltip-tooltip{
    text-align: center;
  }
  & .MuiTooltip-tooltip a{
    color: ${theme.uptimeColors.grayScales.white} !important;
  }
`}`;

const Upload = (props: any) => {
  const { attachmentConfig, canUpload, fullWidth, value, allowedExtensions, locale, warningConfig, localeWarningDialog, errorMessage } = props;
  const [errMsg, setErrMsg] = useState('');
  const [fileUpload, setFileUpload] = useState<File[]>();
  const [onUploadFile, setOnUploadFile] = useState(false);
  const [isOpenWarning, setIsOpenWarning] = useState(false);

  const showWarning = warningConfig?.enabled && JSON.parse(localStorage.getItem(props.warningConfig?.key) || 'true');

  // const extensionsNotAllowed = (
  //   attachmentConfig.not_allowed_extensions || []
  //   ).map((type: any) => mimeType[type]);

  const hasFileTypeAllowed = useCallback((file: File) => {
    const typeFile = file.name.split('.').pop()?.toUpperCase();
    return attachmentConfig?.allowed_extensions?.includes(typeFile);
  }, [attachmentConfig?.allowed_extensions]);

  const getErrorMessage = (file: File) => {
    const isFileEmpty = file.size === 0;
    const isFileSizeExceeded = file.size > maxFileSize;

    if (isFileEmpty) {
      return locale?.errMsg.emptyFile;
    }

    if (!hasFileTypeAllowed(file)) {
      return locale?.errMsg.fileExtentsionNotAllow;
    }

    if (isFileSizeExceeded) {
      return locale?.errMsg.fileSizeExceedsTheAllow.replace("{{fileSizeLimit}}", attachmentConfig?.limited_size || 20);
    }

    return "";
  };

  const handleChange = (files: File[]) => {
    if (files?.length === 0) return;

    const file = files?.[0];

    if (hasFileTypeAllowed(file)) {
      setErrMsg("");
      props.handleChange(files);
      return;
    }

    const error = getErrorMessage(file);
    setErrMsg(error);
  };

  const confirmWarningDialog = (
    <>
      <HlxButton
        label={localeWarningDialog?.ok || warningConfig?.editWarning?.labelButton}
        onClick={() => {
            if(!!onUploadFile) {
              handleChange(fileUpload as File[]);
              setOnUploadFile(false);
            }
            setIsOpenWarning(false);
            localStorage.setItem(warningConfig?.key, 'false');
          }
        }
      />
    </>
  );

  const maxFileSize = useMemo(() => {
    const kilobytes = 1024;
    const megabytes = 1024;
    const bytes = megabytes * kilobytes;
    return attachmentConfig?.limited_size * bytes;
  }, [attachmentConfig?.limited_size]);

  const handleOnDropRejected = (fileRejections: File[]): void => {
    const haveFilesRejection = fileRejections?.length > 0;
    if (!haveFilesRejection) return;

    const file = fileRejections[0];
    const error = getErrorMessage(file);
    setErrMsg(error);
  };

  useEffect(() => {
    setErrMsg(errorMessage || '');
  }, [errorMessage]);

  return (
    <>
    <StyledTooltip 
      title={parse(locale?.dropZoneText) as string} 
      arrow
      placement="top"
    >
      <ContainerStyled className={`upload-container ${!canUpload ? "disabled" : ""} ${
        fullWidth ? " upload-container-full-width" : ""
      }`}>
        <DropzoneArea
          onChange={(files: any) => {!showWarning && handleChange(files) || (showWarning && files.length > 0) && setIsOpenWarning(true); setOnUploadFile(true); setFileUpload(files)}}
          acceptedFiles={allowedExtensions}
          showAlerts={false}
          filesLimit={1}
          initialFiles={value}
          dropzoneText={parse(locale?.dropZoneText) as string}
          showPreviews={false}
          dropzoneProps={{
            disabled: !canUpload,
            minSize: 1,
          }}
          maxFileSize={maxFileSize}
          onDropRejected={handleOnDropRejected}
          inputProps={{
            id: "upl-uploadcomponent-attachement",
            'aria-label': 'Upload Attachment'
          }}

        />

        <div className="upload-config">
          {!!allowedExtensions ?
            <>
              {locale?.configText.text_4}{" "}
              {(attachmentConfig.allowed_extensions || []).join(", ")}.<br></br>
            </>
          :
            <>
              {locale?.configText.text_1}{" "}
              {(attachmentConfig.not_allowed_extensions || []).join(" ,")}.<br></br>
            </>
          }

          {locale?.configText.text_2} {attachmentConfig.limited_size} {locale?.configText.text_3}.
        </div>

        {errMsg && <div className="upload-error">{errMsg}</div>}
      </ContainerStyled>
    </StyledTooltip>
      <UptimeGeneralDialog
        title={warningConfig?.editWarning?.title || localeWarningDialog?.warning}
        content={warningConfig?.editWarning?.content || localeWarningDialog?.msgITARFedRamp}
        isOpen={isOpenWarning}
        buttonSubmit={confirmWarningDialog}
        deviders={false}
        headerBackgroundDefault={false}
        hideButtonCancel={true}
        hideCloseIcon={true}
        commonStyles={{ width: '480px' }}
        handleClose={()=>{}}
        headerDefault={true}
        disableBackdropClick={false}
      />
    </>
  );
};

Upload.propTypes = {
  handleChange: PropTypes.func,
  extension: PropTypes.array,
  attachmentConfig: PropTypes.object,
  value: PropTypes.array,
  canUpload: PropTypes.bool,
  locale: PropTypes.object,
  warningConfig: PropTypes.object,
  localeWarningDialog: PropTypes.object,
};

export const defaultProps = {
  handleChange: () => {},
  extension: [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
  attachmentConfig: {
    allowed_extensions: [
      "JPEG",
      "JPG",
      "PNG",
      "WebP",
      "SVG",
      "GIF",
      "TXT",
      "DOCX",
      "XLSX",
      "DPF",
      "DOC",
    ],
    not_allowed_extensions: [
      "sql",
      "exe",
      "vbs",
      "js",
      "bin",
      "ini",
      "reg",
      "swf",
      "iso",
      "sh",
      "java",
    ],
    limited_size: 20,
  },
  fullWidth: false,
  value: [],
  locale: {
    titleAttachments: "Attach any supporting documentation here:",
    dropZoneText: "Drop file here or <br /><a>Browse Files</a>",
    configText: {
      text_1: "Except file type:",
      text_2: "Limited file size:",
      text_3: "MB",
    },
    errMsg: {
      fileExtentsionNotAllow: "File extension not allowed",
      emptyFile: "Empty files are not allowed",
      fileSizeExceedsTheAllow: "File size exceeds the allowed limit of 20 MB",
    },
  },
  canUpload: false,
  warningConfig: {
    name: "Warning ITAR/FedRamp",
    key: "warningITARFedRamp",
    enabled: false,
    editWarning: {
      title: "",
      content: "",
      labelButton: ""
    }
  },
  localeWarningDialog: {
    warning: "Warning",
    msgITARFedRamp: "System is ITAR/FedRamp compliant.",
    ok: "OK"
  },
};
Upload.defaultProps = defaultProps;

export default Upload;
